import { Injectable } from '@angular/core';
import { addCrosstalkcolumnsToSelectedColumns } from '@ddv/crosstalk';
import { ManagerService } from '@ddv/layout';
import {
    DatasetFetchRuntimeParams,
    NamedQuery,
    Query,
    QueryParams,
    SpecialCaseQueryParams,
    WIDGET_LIFECYCLE_EVENT,
} from '@ddv/models';
import { NamedQueriesService } from '@ddv/named-queries';
import { Observable, map, mergeMap, combineLatest, of } from 'rxjs';

import { DatasetQueryRunnerService } from './dataset-query-runner.service';
import { DatasetQueryToRuntimeParamsConverterService } from './dataset-query-to-runtime-params-converter.service';
import { PublicApiResponseRow, TrebekError } from '../models/trebek';

export interface DatasetFetchResult {
    data: PublicApiResponseRow[];
}

@Injectable()
export class DatasetDatasetFetcherService {
    constructor(
        private readonly namedQueriesService: NamedQueriesService,
        private readonly queryParamsConverter: DatasetQueryToRuntimeParamsConverterService,
        private readonly queryRunner: DatasetQueryRunnerService,
        private readonly managerService: ManagerService,
    ) {}

    fetchData(
        clientCode: string,
        widgetIds: number[],
        namedQueryId: string,
        queryParams: QueryParams | undefined,
        dashboardQueryParam: SpecialCaseQueryParams | undefined,
        isMultiClient: boolean,
    ): Observable<DatasetFetchResult[]> {
        return this.namedQueriesService.fetchNamedQuery(namedQueryId)
            .pipe(
                mergeMap((namedQuery: NamedQuery | undefined) => {
                    if (!namedQuery) {
                        return of([]);
                    }

                    const requests = this.queryParamsConverter.convert(
                        namedQuery.normalizeQueryTypeName(),
                        queryParams,
                        dashboardQueryParam,
                        isMultiClient);

                    return combineLatest(
                        requests.map(({ runtimeParams }) => {
                            try {
                                return this.fetchDataForRuntimeParams(
                                    clientCode,
                                    widgetIds,
                                    namedQuery,
                                    runtimeParams);
                            } catch (error) {
                                this.sendGenericError(widgetIds, error as Error);
                                return of({ data: [] });
                            }
                        }),
                    );
                }),
            );
    }

    private fetchDataForRuntimeParams(
        clientCode: string,
        widgetIds: number[],
        namedQuery: NamedQuery,
        runtimeParams: DatasetFetchRuntimeParams,
    ): Observable<DatasetFetchResult> {
        if (namedQuery.normalizeQueryIsStacked()) {
            const queries = namedQuery.populateStackedQueryTemplate(runtimeParams);
            return this.runQueries(clientCode, widgetIds, namedQuery, queries, runtimeParams);
        } else {
            return this.namedQueriesService.fetchExecutableQuery(namedQuery, runtimeParams)
                .pipe(
                    mergeMap((query) => {
                        if (query.conversationType && query.selectedColumns) {
                            addCrosstalkcolumnsToSelectedColumns(query.selectedColumns);
                        }

                        const queries = [query];
                        return this.runQueries(
                            clientCode,
                            widgetIds,
                            namedQuery,
                            queries,
                            runtimeParams,
                        );
                    }),
                );
        }
    }

    private runQueries(
        clientCode: string,
        widgetIds: number[],
        namedQuery: NamedQuery,
        queries: Query[],
        runtimeParams: DatasetFetchRuntimeParams,
    ): Observable<DatasetFetchResult> {
        return this.queryRunner
            .runQueries(
                clientCode,
                widgetIds,
                queries,
                namedQuery.normalizeQueryEndpoint(),
                namedQuery.normalizeStatusEndpoint(),
                runtimeParams,
            )
            .pipe(
                map(({ data, trebekError }) => {
                    if (trebekError) {
                        this.sendTrebekErrorMessage(widgetIds, trebekError);
                    }

                    // this probably only needs to happen once and we should probably pull it up a function
                    this.sendHideCancelMessage(widgetIds);

                    return { data };
                }),
            );
    }

    private sendHideCancelMessage(widgetIds: number[]): void {
        widgetIds.forEach((widgetId) => {
            this.managerService.sendMessageToExistingWidget(widgetId, WIDGET_LIFECYCLE_EVENT.HIDE_CANCEL);
        });
    }

    private sendTrebekErrorMessage(widgetIds: number[], trebekError: TrebekError): void {
        this.sendErrorMessageToEachWidget(widgetIds, trebekError.message, trebekError.timestamp);
    }

    private sendGenericError(widgetIds: number[], error: Error): void {
        this.sendErrorMessageToEachWidget(widgetIds, error.message);
    }

    private sendErrorMessageToEachWidget(widgetIds: number[], message: string, time?: string): void {
        widgetIds.forEach((widgetId) => {
            this.managerService.sendMessageToExistingWidget(
                widgetId,
                {
                    action: WIDGET_LIFECYCLE_EVENT.ERROR_OCCURRED,
                    exception: {
                        message,
                        time,
                    },
                });
        });
    }
}
